import { FC } from 'react';
import Head from 'next/head';

import {
  addTransformToCloudinaryUrl,
  convertExternalToCloudinary,
  isCloudinaryUrl as checkIsCloudinaryUrl,
} from 'utils/url';
import { useImageLoaded } from 'src/hooks';

import { useGoogleOptimize } from 'utils/google-optimize-manager';

const HEIGHT_360 = 360;
const WIDTH_1000 = 1000;
const WIDTH_600 = 600;
const WIDTH_360 = 360;

const CLOUDINARY_BASE_URL = 'https://res.cloudinary.com/zehitomo/image/upload/';
const OVERRIDE_IMAGES_FOR_JOB_TYPES: Map<string, string> = new Map([
  ['design-renovation-specialist', 'q_auto,w_2048/v1673415853/xg1l1sirbmrfvu1qxtmu.jpg'],
  ['japanese-calligraphy-instructor', 'q_auto,w_2048/v1673415853/yvv3da3i1ovt0p4g1rw6.jpg'],
  ['home-exterior-construction', 'q_auto,w_2048/v1675994647/cinnskgusugd5x1dfstg.jpg'],
  ['zumba-instructor', 'q_auto,w_2048/v1675994776/shxbubkdk2w9scstxdcq.jpg'],
  ['walking-instructor', 'q_auto,w_2048/v1675994773/p0acrik0kzysot5rqcwr.jpg'],
  ['hula-instructor', 'q_auto,w_2048/v1675994770/eabgrnll6xhtlpdqdgvt.jpg'],
  ['catering', 'q_auto,w_2048/v1675994763/qjnrhvezre1mwcuzltvs.jpg'],
]);

export const GOOGLE_OPTIMIZE_HERO_IMAGE_Q_TEST_ID = 'gIjE5zNqRV-lMjlx7UXIfQ';
const GOOGLE_OPTIMIZE_HERO_IMAGE_TEST_VARIATION = '1';
const GOOGLE_OPTIMIZE_DURATION = 100;

const TEST_IMAGES_Q_JOB_TYPES: Map<string, string> = new Map([
  [
    'personal-trainer',
    'c_fill,q_100,w_2048/e5fd915a4fccd3e87962829c0c4c067e_hase6-1024x683_hijrbk.jpg',
  ],
  ['personal-chef', 'c_fill,q_100,w_2048/y64cduicsarhivkvzsqb.jpg'],
  ['bartender', 'c_fill,q_100,w_2048/v1515490522/bzynwy2988flnfoonvzv.jpg'],
  ['vocal-instructor', 'c_fill,q_100,w_2048/wydk0ulosp5fwcgkj7ch.jpg'],
  ['walking-instructor', 'c_fill,q_100,w_2048/v1675994773/p0acrik0kzysot5rqcwr.jpg'],
  ['yoga-instructor', 'c_fill,q_100,w_2048/v1515490876/h1kpcpceabkz0tyxjhji.jpg'],
  ['manicurist', 'c_fill,q_100,w_2048/ffbic5p7nr9gtryj0cjn.jpg'],
  ['swimming-lessons', 'c_fill,q_100,w_2048/v1515490876/m2duuhn0phoupxrqlmue.jpg'],
  ['painting-sketching-instructor', 'c_fill,q_100,w_2048/v1515490877/zjf1e2kwzqkmp6enfvxo.jpg'],
]);

const POSITION_FOR_JOB_TYPES: Map<string, string> = new Map([
  ['design-renovation-specialist', 'left'],
  ['sign-teacher', '40%'],
  ['home-cooking-instructor', '35%'],
  ['health-diet-cooking-instructor', '35%'],
]);

interface Props {
  alt?: string;
  bgImg: string;
  bgImgHeight?: string | number;
  bgImgObjectFit?: 'cover' | 'contain';
  bgImgPosition?: 'static' | 'absolute';
  bgImgWidth?: string | number;
  bgPos?: string;
  fullHeight?: boolean;
  isResponsive?: boolean;
  jobTypeId?: string;
  onImageLoadError?: () => void;
  onLoadingComplete?: ({ width, height }: { height: number; width: number }) => void;
}

const Jumbotron: FC<Props> = ({
  alt = '',
  bgImg = '',
  bgPos = 'right',
  bgImgObjectFit = 'cover',
  bgImgPosition = 'static',
  bgImgHeight,
  bgImgWidth,
  jobTypeId = '',
  onLoadingComplete,
  onImageLoadError,
  children = null,
  fullHeight = false,
  isResponsive = false,
}) => {
  const isTestVariation =
    useGoogleOptimize(GOOGLE_OPTIMIZE_HERO_IMAGE_Q_TEST_ID, GOOGLE_OPTIMIZE_DURATION) ===
      GOOGLE_OPTIMIZE_HERO_IMAGE_TEST_VARIATION && TEST_IMAGES_Q_JOB_TYPES.has(jobTypeId);

  // https://github.com/vercel/next.js/blob/canary/packages/next/client/image.tsx#L325
  // https://stackoverflow.com/questions/39777833/image-onload-event-in-isomorphic-universal-react-register-event-after-image-is
  // UseImageLoaded is a work around for the case where the image is loaded before the component is mounted.
  // Images with non 200 status codes may not trigger the onError event so we need to check the image dimensions
  // on load to determine whether the image is broken.
  const [ref] = useImageLoaded(onLoadingComplete);

  const isCloudinaryUrl = checkIsCloudinaryUrl(bgImg);
  const isStaticUrl = bgImg.includes('/static/images');
  const backgroundImageUrl =
    isCloudinaryUrl || isStaticUrl
      ? bgImg
      : convertExternalToCloudinary(bgImg, { shouldTransform: false });
  const imgUrl1000 = addTransformToCloudinaryUrl(backgroundImageUrl, {
    isCloudinaryFetchTransform: !isCloudinaryUrl,
    crop: 'fill',
    dpr: '1.0',
    // eslint-disable-next-line no-magic-numbers
    quality: isTestVariation ? 100 : 80,
    shouldUseProxy: true,
    width: WIDTH_1000,
  });
  const imgUrl600 = addTransformToCloudinaryUrl(backgroundImageUrl, {
    isCloudinaryFetchTransform: !isCloudinaryUrl,
    crop: 'fill',
    dpr: '1.0',
    // eslint-disable-next-line no-magic-numbers
    quality: isTestVariation ? 100 : 80,
    shouldUseProxy: true,
    width: WIDTH_600,
  });
  const imgUrl360 = addTransformToCloudinaryUrl(backgroundImageUrl, {
    isCloudinaryFetchTransform: !isCloudinaryUrl,
    crop: 'fill',
    dpr: '1.0',
    height: HEIGHT_360,
    // eslint-disable-next-line no-magic-numbers
    quality: isTestVariation ? 100 : 80,
    shouldUseProxy: true,
    width: WIDTH_360,
  });

  const hasExplicitHeight = fullHeight;
  const responsiveDimensions = !hasExplicitHeight && isResponsive && 'height: 100%;\nwidth:100%';

  let overrideImageUrl;
  if (OVERRIDE_IMAGES_FOR_JOB_TYPES.has(jobTypeId)) {
    const img = OVERRIDE_IMAGES_FOR_JOB_TYPES.get(jobTypeId);
    if (img) {
      overrideImageUrl = `${CLOUDINARY_BASE_URL}${img}`;
    }
  }

  if (isTestVariation && TEST_IMAGES_Q_JOB_TYPES.has(jobTypeId)) {
    const img = TEST_IMAGES_Q_JOB_TYPES.get(jobTypeId);
    if (img) {
      overrideImageUrl = `${CLOUDINARY_BASE_URL}${img}`;
    }
  }

  let overrideObjectPosition;
  if (overrideImageUrl && POSITION_FOR_JOB_TYPES.has(jobTypeId)) {
    const pos = POSITION_FOR_JOB_TYPES.get(jobTypeId);
    if (pos) {
      overrideObjectPosition = pos;
    }
  }

  /**
   * IMPORTANT NOTE
   * The bgImg should never be lazy loaded, because it is rendered on load in the client viewport and it does impact LCP
   * Google Lighthouse or even Page Speed Insight might give warning about the fact that this iamge is not lazy loaded, but IGNORE this warning
   * Because if we lazy loaded the jumbotron image, it will drastically increase LCP.
   *
   * Not all image should be lazy loaded.
   * Only images that initially appear outside the viewport can be lazy loaded.
   */

  return (
    <section className="jumbotron" data-test-id="jumbotron">
      {bgImg && (
        <img
          ref={ref}
          src={overrideImageUrl ?? imgUrl1000}
          srcSet={`${overrideImageUrl ?? imgUrl600} 999w, ${overrideImageUrl ?? imgUrl360} 599w`}
          alt={alt}
          onError={onImageLoadError}
          height={bgImgHeight}
          width={bgImgWidth}
        />
      )}
      {bgImg && (
        <Head>
          <link
            rel="preload"
            as="image"
            href={imgUrl1000}
            // @ts-expect-error not property type
            imagesrcset={`${imgUrl600} 999w, ${imgUrl360} 599w`}
          />
        </Head>
      )}
      {children}
      <style jsx>
        {`
          .jumbotron {
            padding: 0;
            position: relative;
            ${fullHeight ? 'height: 700px;' : ''}
            ${responsiveDimensions ? responsiveDimensions : ''}
          }
          @media screen and (max-width: 768px) {
            .jumbotron {
              ${fullHeight ? 'height: 90vh;' : ''}
              ${responsiveDimensions ? responsiveDimensions : ''}
            }
          }
          .jumbotron > img {
            width: 100%;
            object-fit: ${bgImgObjectFit};
            object-position: ${overrideObjectPosition ? overrideObjectPosition : bgPos};
            position: ${bgImgPosition};
            z-index: -1;
            height: 100%;
          }
        `}
      </style>
    </section>
  );
};

export type { Props as JumbotronProps };
export default Jumbotron;
